import { useRef } from "react";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/v2/EmptyState";
import {
  useAddBookingFile,
  useBookingFiles,
  useDeleteBookingFile,
  type BookingFile,
} from "@/lib/booking-attachments";
import { useToast } from "@/components/v2/Toast";

/**
 * Files tab — Pencil `TUZ1P`. Operator-uploaded attachments backed by
 * `/api/v1/bookings/{id}/files`. Bytes live in MinIO; each row carries a
 * 30-min presigned download URL the browser can hit directly.
 */
export function BookingFilesTab({ bookingId }: { bookingId: string }) {
  const list = useBookingFiles(bookingId);
  const add = useAddBookingFile(bookingId);
  const del = useDeleteBookingFile(bookingId);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;
    Array.from(fileList).forEach((f) => {
      add.mutate(f, {
        onError: (err) =>
          toast.error("Couldn't upload", (err as Error).message),
      });
    });
    e.target.value = "";
  };

  const onDelete = (file: BookingFile) => {
    if (!confirm(`Delete ${file.filename}?`)) return;
    del.mutate(file.id, {
      onError: (err) =>
        toast.error("Couldn't delete", (err as Error).message),
    });
  };

  const files = list.data?.entries ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">
          Files{" "}
          <span className="text-muted-foreground font-normal">
            ({files.length})
          </span>
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={add.isPending}
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          {add.isPending ? "Uploading…" : "Upload"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={onPick}
        />
      </div>

      {list.isLoading && (
        <p className="text-sm text-muted-foreground">Loading files…</p>
      )}
      {list.isError && (
        <p className="text-sm text-destructive">
          {(list.error as Error).message}
        </p>
      )}

      {!list.isLoading && !list.isError && files.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No files attached"
          description="Contracts, mood boards, references, equipment lists — anything related to this booking lives here."
          action={
            <Button size="sm" onClick={() => inputRef.current?.click()}>
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload first file
            </Button>
          }
        />
      ) : (
        <ul className="rounded-lg border bg-card divide-y">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-3 px-4 py-3"
            >
              <div className="h-9 w-9 rounded-md bg-muted/50 inline-flex items-center justify-center shrink-0 text-muted-foreground">
                <FileText className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{f.filename}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {humanSize(f.bytes)} · uploaded by {f.uploadedBy}
                </p>
              </div>
              <a
                href={f.downloadUrl}
                download={f.filename}
                target="_blank"
                rel="noreferrer"
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                aria-label={`Download ${f.filename}`}
                title="Download"
              >
                <Download className="h-4 w-4" />
              </a>
              <button
                type="button"
                onClick={() => onDelete(f)}
                disabled={del.isPending}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-status-bg-cancelled hover:text-status-cancelled disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={`Delete ${f.filename}`}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
